import { useCallback } from 'react';
import { useStore } from '@hub/helpers/use-store';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

/**
 * Query keys for Rise ID related queries
 */
const RISE_ID_QUERY_KEYS = {
  RISE_ID: 'rise-id',
  USER_PAYMENT_DATA: 'user-payment-data',
} as const;

/**
 * Hook for fetching Rise ID from the payout store
 */
export const useRiseId = () => {
  const {
    dataStore: { payoutStore },
  } = useStore();

  return useQuery({
    queryKey: [RISE_ID_QUERY_KEYS.RISE_ID],
    queryFn: () => payoutStore.getRiseId(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * Hook for updating user payment data with Rise ID
 */
export const useUpdatePaymentData = () => {
  const {
    dataStore: { userStore },
  } = useStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (riseId: string) => {
      return userStore.updatePaymentData({
        rise: {
          riseId,
        },
      });
    },
    onSuccess: () => {
      // Invalidate user payment data cache
      queryClient.invalidateQueries({
        queryKey: [RISE_ID_QUERY_KEYS.USER_PAYMENT_DATA],
      });
    },
    onError: (error) => {
      console.error('Failed to update payment data:', error);
    },
  });
};

/**
 * Combined hook for Rise ID validation workflow
 * Automatically fetches Rise ID and updates payment data if needed
 */
export const useRiseIdValidation = () => {
  const {
    dataStore: { userStore, payoutStore },
  } = useStore();
  const updatePaymentData = useUpdatePaymentData();

  const currentUser = userStore.currentUser;
  const riseId = currentUser?.paymentData?.rise?.riseId;
  const riseInvitationDate = currentUser?.paymentData?.rise?.invitedAt;

  // Only fetch Rise ID if user has invitation but no Rise ID
  const shouldFetchRiseId = !riseId && !!riseInvitationDate;

  const riseIdQuery = useQuery({
    queryKey: [RISE_ID_QUERY_KEYS.RISE_ID, currentUser?.id],
    queryFn: async () => {
      const result = await payoutStore.getRiseId();

      // Automatically update payment data if Rise ID is returned
      if (result?.riseId) {
        updatePaymentData.mutate(result.riseId);
      }

      return result;
    },
    enabled: shouldFetchRiseId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const validateRiseId = useCallback(async () => {
    if (shouldFetchRiseId) {
      return riseIdQuery.refetch();
    }
    return Promise.resolve();
  }, [shouldFetchRiseId, riseIdQuery]);

  return {
    riseId,
    riseInvitationDate,
    shouldFetchRiseId,
    isValidating: riseIdQuery.isFetching || updatePaymentData.isPending,
    isError: riseIdQuery.isError || updatePaymentData.isError,
    error: riseIdQuery.error || updatePaymentData.error,
    validateRiseId,
    refetch: riseIdQuery.refetch,
  };
};
